import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { TranslationProvider, useTranslation } from "../../shared/contexts/TranslationContext";
import { LanguageToggle } from "../../shared/components/LanguageToggle";
import { AdminLink } from "../../shared/components/AdminLink";

import { FeatureSection } from "./components/FeatureSection";
import { GridSection } from "./components/GridSection";
import { SplitSection } from "./components/SplitSection";

function FiscalEmissionV2Inner({ lang, setLang }) {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t("heroTitle")} | Native v2`;

    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

      if (!["ArrowRight", "ArrowDown", "Enter", "ArrowLeft", "ArrowUp"].includes(e.key)) return;

      const sections = Array.from(document.querySelectorAll('section[id^="section-"]'));
      if (!sections.length) return;

      let currentIndex = 0;
      let minDistance = Infinity;

      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top);
        if (distance < minDistance) {
          minDistance = distance;
          currentIndex = index;
        }
      });

      let targetIndex = currentIndex;
      if (["ArrowRight", "ArrowDown", "Enter"].includes(e.key)) {
        e.preventDefault();
        targetIndex = Math.min(currentIndex + 1, sections.length - 1);
      } else if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        targetIndex = Math.max(currentIndex - 1, 0);
      }

      if (targetIndex !== currentIndex) {
        sections[targetIndex].scrollIntoView({ behavior: "smooth" });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [t]);

  return (
    <div className="min-h-screen w-full bg-[#f8f4f0] text-[#1c1c1c] font-sans overflow-y-auto overflow-x-hidden relative selection:bg-black selection:text-white">
      
      {/* Navigation & Controls Overlay */}
      <div className="fixed top-6 left-6 z-[100]">
        <Link 
          to="/" 
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 hover:bg-black/10 transition-colors text-black/70 hover:text-black font-medium text-sm no-underline backdrop-blur-md"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back to Hub</span>
        </Link>
      </div>

      <div className="fixed top-2 right-4 z-[100] flex items-center gap-2">
        <div className="bg-white/80 backdrop-blur-md rounded-full shadow-sm border border-black/5 p-1 flex items-center">
          <LanguageToggle lang={lang} setLang={setLang} className="" />
        </div>
        <AdminLink className="flex-shrink-0" />
      </div>

      {/* Hero Section */}
      <section id="section-0" className="relative w-full min-h-[90vh] flex items-center justify-center pt-24 pb-16 px-6 lg:px-16 bg-[#F6EFE9] border-b border-black/5">
        <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          <div className="flex flex-col gap-6 z-10 lg:pr-8">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#1c1c1c] underline decoration-[4px] underline-offset-[8px]">
              {t("heroTitle")}
            </h2>
            <h1 className="text-6xl md:text-[5.5rem] font-black leading-[1.05] tracking-tighter text-[#1c1c1c] mt-2">
              {t("heroSubtitle")}
            </h1>
            
            <p className="text-[15px] text-[#1c1c1c] max-w-lg leading-[1.8] font-mono mt-6 font-medium">
              {t("heroDescription")}
            </p>
            
            <div className="flex flex-wrap gap-4 mt-6">
              <button className="bg-[#18181b] text-white px-7 py-3.5 rounded font-semibold text-sm hover:bg-black transition-colors shadow-sm">
                {t("ctaExplore")}
              </button>
              <button className="bg-transparent text-[#18181b] border border-[#18181b] px-7 py-3.5 rounded font-semibold text-sm hover:bg-black/5 transition-all">
                {t("ctaDemo")}
              </button>
            </div>
          </div>

          {/* Right Graphic - Custom Tailwind Replica of Gamma UI */}
          <div className="relative w-full h-[600px] flex items-center justify-center lg:pl-12">
            {/* Main Application Window */}
            <div className="relative w-[480px] h-[520px] bg-white rounded-xl shadow-2xl border border-black/5 flex flex-col overflow-hidden rotate-[1deg] hover:rotate-0 transition-transform duration-500 origin-bottom-right">
              
              {/* Window Header */}
              <div className="h-10 w-full border-b border-black/5 flex items-center px-4 gap-2 bg-[#fdfcfb]">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                <div className="ml-auto w-12 h-3 bg-black/5 rounded-full"></div>
              </div>

              {/* Window Body */}
              <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <div className="w-14 h-full border-r border-black/5 bg-[#faf9f8] flex flex-col items-center py-4 gap-4">
                  <div className="w-6 h-6 rounded bg-black/10"></div>
                  <div className="w-6 h-6 rounded bg-black/5"></div>
                  <div className="w-6 h-6 rounded bg-black/5"></div>
                  <div className="w-6 h-6 rounded bg-black/5 mt-auto mb-2"></div>
                </div>
                
                {/* Main Content Area */}
                <div className="flex-1 p-6 flex flex-col gap-6 relative">
                  
                  {/* Enterprise Section */}
                  <div className="bg-[#fff9f2] border border-[#f5e6d3] p-4 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-4 h-4 rounded-full bg-orange-200"></div>
                      <span className="font-bold text-sm text-[#1c1c1c]">Enterprise</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="w-full h-2 bg-black/5 rounded-full"></div>
                      <div className="w-3/4 h-2 bg-black/5 rounded-full"></div>
                      <div className="w-5/6 h-2 bg-black/5 rounded-full"></div>
                    </div>
                  </div>

                  {/* Dxmopres Section */}
                  <div className="bg-white border border-black/5 p-4 rounded-lg shadow-sm mt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="font-bold text-sm text-[#1c1c1c]">Dxmopres</span>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-400"></div><div className="w-1/2 h-2 bg-black/5 rounded-full"></div></div>
                      <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-400"></div><div className="w-2/3 h-2 bg-black/5 rounded-full"></div></div>
                      <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-400"></div><div className="w-1/3 h-2 bg-black/5 rounded-full"></div></div>
                    </div>
                  </div>

                  {/* Floating XML Cards */}
                  <div className="absolute right-4 top-8 flex flex-col gap-4 drop-shadow-xl translate-x-8 -translate-y-4">
                    
                    {/* XML Circular Badge */}
                    <div className="w-20 h-20 rounded-full bg-[#fde8d7] border-4 border-white shadow-lg flex items-center justify-center ml-auto">
                      <span className="font-bold text-orange-800 tracking-tighter">XML</span>
                    </div>

                    {/* XML2 Document Card */}
                    <div className="w-28 h-36 bg-white border border-black/5 rounded-lg shadow-xl p-3 flex flex-col relative rotate-[-2deg]">
                      <div className="font-mono text-xs font-bold text-black/40 mb-2">XML2</div>
                      <div className="flex flex-col gap-1.5 mt-auto">
                        <div className="w-full h-1.5 bg-orange-100 rounded-full"></div>
                        <div className="w-4/5 h-1.5 bg-orange-100 rounded-full"></div>
                      </div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 border-2 border-orange-500/30 rounded-full flex items-center justify-center rotate-45">
                        <span className="text-[8px] font-bold text-orange-500/50 uppercase tracking-widest">Valid</span>
                      </div>
                    </div>

                    {/* Protocol Document Card */}
                    <div className="w-28 h-36 bg-white border border-black/5 rounded-lg shadow-xl p-3 flex flex-col relative rotate-[3deg] ml-4">
                      <div className="w-full h-1.5 bg-blue-100 rounded-full mb-1.5 mt-2"></div>
                      <div className="w-3/4 h-1.5 bg-blue-100 rounded-full"></div>
                      <div className="absolute bottom-2 right-2 w-14 h-14 border-2 border-red-500/40 rounded-full flex items-center justify-center -rotate-12">
                        <span className="text-[6px] font-bold text-red-500/60 uppercase tracking-widest border-t border-b border-red-500/40 py-0.5">Protocolo</span>
                      </div>
                    </div>

                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Section 1: End-to-End Flow Process */}
      <section id="section-1" className="relative w-full min-h-[90vh] flex flex-col items-center justify-start pt-12 pb-16 px-6 lg:px-16 bg-[#F6EFE9] border-b border-black/5">
        <div className="max-w-5xl w-full flex flex-col items-center text-center gap-16">
          
          <div className="flex flex-col gap-6 items-center">
            <h2 className="text-5xl md:text-6xl font-black leading-[1.1] tracking-tighter text-[#1c1c1c]">
              {t("endToEndSubtitle")}
            </h2>
            <p className="text-[15px] text-[#1c1c1c] max-w-3xl leading-[1.8] font-mono font-medium">
              {t("endToEndDesc1")}
            </p>
          </div>

          {/* Process Diagram */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-0 my-12 scale-125 md:scale-[0.88] md:my-16 origin-center">
            
            {/* Step 1: Setup */}
            <div className="flex flex-col items-center relative z-40">
              <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-[16px] md:border-[20px] border-[#1c1c1c] flex items-center justify-center bg-[#F6EFE9] relative z-10">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path></svg>
              </div>
              <span className="mt-10 text-2xl md:text-3xl font-black text-[#1c1c1c] tracking-tight">Setup</span>
            </div>

            <div className="hidden md:block w-12 h-20 bg-[#1c1c1c] -ml-6 relative z-30 transform [clip-path:polygon(0_0,100%_50%,0_100%)]"></div>

            {/* Step 2: Draft */}
            <div className="flex flex-col items-center relative z-30 -ml-6">
              <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-[16px] md:border-[20px] border-[#1c1c1c] flex items-center justify-center bg-[#F6EFE9] relative z-10">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M10.5 13.5l4-4"></path><path d="M8.5 15.5l1-1"></path><path d="M12 17l1.5-1.5"></path></svg>
              </div>
              <span className="mt-10 text-2xl md:text-3xl font-black text-[#1c1c1c] tracking-tight">Draft</span>
            </div>

            <div className="hidden md:block w-12 h-20 bg-[#1c1c1c] -ml-6 relative z-20 transform [clip-path:polygon(0_0,100%_50%,0_100%)]"></div>

            {/* Step 3: Authorize */}
            <div className="flex flex-col items-center relative z-20 -ml-6">
              <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-[16px] md:border-[20px] border-[#1c1c1c] flex items-center justify-center bg-[#F6EFE9] relative z-10">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 5.5l-4 4"></path><path d="M17 5.5l4 4"></path><path d="M10 14c-1.5 0-3-.5-4.5-1.5s-2.5-2.5-3-4C3 9 3.5 10 4.5 11s2.5 1.5 4 1.5c1 0 2-.5 3-1.5s2.5-2.5 4-2.5c1.5 0 3 .5 4.5 1.5s2.5 2.5 3 4c-.5-.5-1-1.5-2-2.5s-2.5-1.5-4-1.5c-1 0-2 .5-3 1.5s-2.5 2.5-4 2.5z"></path></svg>
              </div>
              <span className="mt-10 text-2xl md:text-3xl font-black text-[#1c1c1c] tracking-tight">Authorize</span>
            </div>

            <div className="hidden md:block w-12 h-20 bg-[#1c1c1c] -ml-6 relative z-10 transform [clip-path:polygon(0_0,100%_50%,0_100%)]"></div>

            {/* Step 4: Archive */}
            <div className="flex flex-col items-center relative z-10 -ml-6">
              <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-[16px] md:border-[20px] border-[#1c1c1c] flex items-center justify-center bg-[#F6EFE9] relative z-10">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2z"></path><line x1="9" y1="14" x2="15" y2="14"></line></svg>
              </div>
              <span className="mt-10 text-2xl md:text-3xl font-black text-[#1c1c1c] tracking-tight">Archive</span>
            </div>

          </div>

          <p className="text-lg md:text-xl text-[#1c1c1c] max-w-4xl leading-[1.8] font-mono mt-4 font-medium">
            {t("endToEndDesc2")}
          </p>

        </div>
      </section>

      {/* Section 2: FIG */}
      <section id="section-2" className="relative w-full min-h-[90vh] flex flex-col items-center justify-start pt-12 pb-16 px-6 lg:px-16 bg-[#F6EFE9] border-t border-black/5">
        <div className="max-w-6xl w-full flex flex-col items-center text-center gap-10">
          
          <div className="flex flex-col gap-6 items-center">
            <h2 className="text-5xl md:text-6xl font-black leading-[1.1] tracking-tighter text-[#1c1c1c]">
              FIG — Fiscal Integration Gateway
            </h2>
            <p className="text-lg md:text-xl text-[#1c1c1c] max-w-4xl leading-[1.8] font-mono font-medium">
              {t("figDesc")}
            </p>
          </div>

          <div className="relative w-full flex items-center justify-center">
            <img 
              src="/presentation-hub/images/fiscal-emission-v2/fig-diagram.avif" 
              alt="FIG Diagram" 
              className="w-full max-w-5xl object-contain drop-shadow-sm scale-95 md:scale-95 transform mt-16"
            />
          </div>

        </div>
      </section>

      <GridSection 
        id="section-3"
        title="Onboarding"
        subtitle={t("onboardingTitle")}
        description={t("onboardingDesc")}
        items={{
          1: { title: t("onboardingItems.issuer.title"), desc: t("onboardingItems.issuer.desc") },
          2: { title: t("onboardingItems.cert.title"), desc: t("onboardingItems.cert.desc") },
          3: { title: t("onboardingItems.doc.title"), desc: t("onboardingItems.doc.desc") },
          4: { title: t("onboardingItems.golive.title"), desc: t("onboardingItems.golive.desc") }
        }}
      />

      <GridSection 
        id="section-4"
        title="Intelligence"
        subtitle={t("masterDataTitle")}
        description={t("masterDataDesc")}
        items={{
          1: { title: t("masterDataItems.product.title"), desc: t("masterDataItems.product.desc") },
          2: { title: t("masterDataItems.service.title"), desc: t("masterDataItems.service.desc") },
          3: { title: t("masterDataItems.parties.title"), desc: t("masterDataItems.parties.desc") },
          4: { title: t("masterDataItems.rules.title"), desc: t("masterDataItems.rules.desc") }
        }}
      />

      <FeatureSection 
        id="section-5"
        title="Sales & Retail"
        subtitle={t("nfeTitle")}
        description={t("nfeDesc")}
        align="left"
      >
        <div className="flex flex-col gap-6 w-full max-w-md">
          <div className="bg-white p-6 rounded-xl shadow-lg border border-black/5">
            <h4 className="font-bold text-lg mb-2">{t("nfeItems.sales.title")}</h4>
            <p className="text-black/60 font-mono text-sm">{t("nfeItems.sales.desc")}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg border border-black/5">
            <h4 className="font-bold text-lg mb-2">{t("nfeItems.retail.title")}</h4>
            <p className="text-black/60 font-mono text-sm">{t("nfeItems.retail.desc")}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg border border-black/5 border-l-4 border-l-red-500">
            <h4 className="font-bold text-lg mb-2">{t("nfeItems.rejection.title")}</h4>
            <p className="text-black/60 font-mono text-sm">{t("nfeItems.rejection.desc")}</p>
          </div>
        </div>
      </FeatureSection>

      <SplitSection 
        id="section-6"
        title={t("nfseTitle")}
        description={t("nfseDesc")}
        challengeTitle={t("nfseChallenge")}
        challengeDesc={t("nfseChallengeDesc")}
        solutionTitle={t("nfseSolution")}
        solutionItems={t("nfseSolutionItems") || []}
      />

      <GridSection 
        id="section-7"
        title="Logistics"
        subtitle={t("transportTitle")}
        description={t("transportDesc")}
        items={{
          1: { title: t("transportItems.freight.title"), desc: t("transportItems.freight.desc") },
          2: { title: t("transportItems.cte.title"), desc: t("transportItems.cte.desc") },
          3: { title: t("transportItems.mdfe.title"), desc: t("transportItems.mdfe.desc") },
          4: { title: t("transportItems.closure.title"), desc: t("transportItems.closure.desc") }
        }}
      />

      {/* Section 8: Lifecycle */}
      <section id="section-8" className="relative w-full min-h-[90vh] flex flex-col items-center justify-start pt-16 pb-24 px-6 lg:px-16 bg-[#F6EFE9] border-t border-black/5">
        <div className="max-w-6xl w-full flex flex-col items-center text-center gap-10">
          
          <div className="flex flex-col gap-6 items-center">
            <h2 className="text-sm md:text-md font-bold tracking-widest text-[#1c1c1c] mb-1 uppercase border-b-2 border-black inline-block pb-1">
              {t("eventsTitle") || "Lifecycle"}
            </h2>
            <h3 className="text-4xl md:text-5xl font-black leading-[1.1] tracking-tighter text-[#1c1c1c]">
              Events & Legal Lifecycle Management
            </h3>
            <p className="text-[15px] md:text-lg text-[#1c1c1c] max-w-4xl leading-[1.8] font-mono mt-2 font-medium">
              {t("eventsDesc")} {t("eventsSub")}
            </p>
          </div>

          {/* Vertical Timeline Chart */}
          <div className="relative w-full max-w-2xl flex flex-col gap-12 md:gap-16 py-10 mt-8">
            {/* Central Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black/20 -translate-x-1/2" />
            
            {[
              { label: "Draft", side: "left" },
              { label: "Authorized", side: "right" },
              { label: "Corrected", side: "left" },
              { label: "Cancelled", side: "right" },
              { label: "Closed", side: "left" },
              { label: "Archived", side: "right" }
            ].map((item, i) => (
              <div key={i} className="relative flex w-full items-center">
                
                {/* Left Half */}
                <div className="w-1/2 flex items-center justify-end">
                  {item.side === 'left' && (
                    <>
                      <span className="text-2xl md:text-3xl font-black tracking-tight text-[#1c1c1c]">{item.label}</span>
                      <div className="w-8 md:w-16 h-px bg-[#1c1c1c] ml-4 md:ml-6"></div>
                    </>
                  )}
                </div>

                {/* Center Dot */}
                <div className="absolute left-1/2 top-1/2 w-3 h-3 rounded-full bg-[#1c1c1c] -translate-x-1/2 -translate-y-1/2 z-10" />

                {/* Right Half */}
                <div className="w-1/2 flex items-center justify-start">
                  {item.side === 'right' && (
                    <>
                      <div className="w-8 md:w-16 h-px bg-[#1c1c1c] mr-4 md:mr-6"></div>
                      <span className="text-2xl md:text-3xl font-black tracking-tight text-[#1c1c1c]">{item.label}</span>
                    </>
                  )}
                </div>

              </div>
            ))}
          </div>

        </div>
      </section>

      <GridSection 
        id="section-9"
        title="Centralization"
        subtitle={t("controlTitle")}
        description={t("controlDesc")}
        items={{
          1: { title: t("controlItems.layer.title"), desc: t("controlItems.layer.desc") },
          2: { title: t("controlItems.model.title"), desc: t("controlItems.model.desc") },
          3: { title: t("controlItems.truth.title"), desc: t("controlItems.truth.desc") },
          4: { title: t("controlItems.connection.title"), desc: t("controlItems.connection.desc") }
        }}
      />

    </div>
  );
}

export function FiscalEmissionV2Deck() {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("fe_v2_lang");
      if (stored === "pt" || stored === "en" || stored === "es") return stored;
      const nav = (navigator.language || "en").toLowerCase();
      if (nav.startsWith("es")) return "es";
      return nav.startsWith("pt") ? "pt" : "en";
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("fe_v2_lang", lang);
  }, [lang]);

  return (
    <TranslationProvider deck="fiscal-emission-v2" lang={lang}>
      <FiscalEmissionV2Inner lang={lang} setLang={setLang} />
    </TranslationProvider>
  );
}
